<template>
  <div class="contact-card">
    <div v-if="!loading">
      <div class="d-flex line-1-25 top-section">
        <div class="width-70 left-section">
          <div
            class="name float-l width-100 txt-blue"
            v-if="profileData.fullName"
          >
            {{ profileData.fullName }}
          </div>
          <div class="title float-l width-100" v-if="profileData.title">
            {{ profileData.title }}
          </div>
        </div>
        <div class="width-30 flex-c but-holder" v-if="canShowButton()">
          <!-- show get email button only if domain is present -->
          <Find-Email-Button
            :masterContactId="profileData.mcId"
            :fromBulk="true"
            v-on:response="updatePhoneAndEmailFromSmtp"
            :showUpgradePopup="showUpgradePopup"
            :enableReviewPopup="enableReviewPopup"
            :showActivationPopup="showActivationError"
            :showLinkedInConnect="showLinkedInConnectPrompt"
          />
        </div>
      </div>
      <div class="flex-c company-name" v-if="profileData.companyName">
        <Company-Icon />
        <span class="fn-13">{{ profileData.companyName }}</span>
      </div>
      <div class="error" v-if="errorMsg && errorMsg.msg && !profileData.email">
        <span>{{ errorMsg.msg }}</span>
      </div>
      <div
        v-if="
          !profileData.companyName && !profileData.email && !profileData.domain
        "
        class="flex-c no-company"
      >
        <Add-Domain-Find-Email-But
          class="flex-c"
          :text="'Add Company to find Email'"
          :type="'company'"
          :data="profileData"
          v-on:newDomainSubmitted="findEmailWithCustomDomain"
        />
      </div>
      <div
        v-if="
          profileData.companyName && !profileData.domain && !profileData.email
        "
        class="flex-c no-company"
      >
        <Add-Domain-Find-Email-But
          class="flex-c"
          :text="'Add Domain to find Email'"
          :type="'domain'"
          :data="profileData"
          v-on:newDomainSubmitted="findEmailWithCustomDomain"
        />
      </div>
      <div class="flex-c email-hldr" v-if="profileData.email">
        <Email-Icon viewBox="2 5 22 12" width="14px" height="13px" />
        <div class="flex-c width-100 p-rel">
          <span
            @mouseover="showCopyMsg = true"
            @mouseout="showCopyMsg = false"
            :class="[{ cursor: !canShowButton() }, 'email noselect']"
            @click="
              isContactOwned
                ? copyClipboard(profileData.mcId, profileData.email, 'email')
                : $event.preventDefault()
              showCopyMsg = false
            "
            >{{ profileData.email }}</span
          >
          <div v-if="showCopyMsg && !canShowButton()" class="tooltip">
            Click to copy
          </div>
          <div class="copy-section flex-c" v-if="!canShowButton()">
            <input
              type="text"
              :value="profileData.email"
              :id="profileData.mcId + 'email'"
              class="noVisibility"
            />
            <Copy-Icon
              class="copy-icon"
              @click="
                copyClipboard(profileData.mcId, profileData.email, 'email')
              "
            />
          </div>
          <span class="margin-auto" v-if="profileData.emailDeliverability">
            <Email-confidence-card
              class="fn-13"
              :score="profileData.emailDeliverability"
            />
          </span>
        </div>
      </div>
      <Upgrade-Popup
        v-if="showPopup"
        :upgradePopupType="upgradePopupType"
        v-on:clickedOutside="showPopup = false"
      />
      <Activation-Popup
        v-if="showActivationPopup"
        v-on:closed="showActivationPopup = false"
      />

      <Linkedin-Connect-Popup
        v-if="showLinkedInConnect"
        v-on:success="
          showLinkedInConnect = false
          findEmail(true)
        "
        v-on:failed="
          showLinkedInConnect = false
          showUpgradePopup('li_connect_multi')
        "
        v-on:close="showLinkedInConnect = false"
        v-on:skip="
          showLinkedInConnect = false
          showUpgradePopup('li_connect_skip')
        "
      />
      <div
        class="flex-c no-domain"
        v-if="!canShowButton() && errorMsg && profileData.domain"
      >
        <Add-Domain-Find-Email-But
          class="flex-c"
          :text="'Try for a different domain'"
          :type="'domain'"
          :data="profileData"
          v-on:newDomainSubmitted="findEmailWithCustomDomain"
        />
      </div>

      <div v-if="profileData.phone" class="p-t-7p flex-c phonecard-grid">
        <div
          class="txt-blue flex-c"
          v-for="(ele, index) in profileData.phone"
          :key="index"
        >
          <Phone-Card
            v-if="ele.score == 'high'"
            :profileData="profileData"
            :phone="ele"
            :contactsOwned="isContactOwned"
          />
        </div>
      </div>
    </div>
    <div v-else>
      <div class="d-flex line-1-25 top-section">
        <div class="width-70 left-section">
          <div
            class="name float-l width-100 txt-blue linear-background m-b-5px"
          ></div>
          <div class="title float-l width-100 linear-background"></div>
        </div>
        <div class="width-30 flex-c but-holder">
          <button class="but linear-background"></button>
        </div>
      </div>
      <div class="flex-c company-name">
        <Company-Icon />
        <span class="fn-13 linear-background"></span>
      </div>
      <div class="flex-c email-hldr">
        <Email-Icon viewBox="2 5 22 12" width="17px" height="13px" />
        <div class="flex-c width-100">
          <span class="email linear-background"></span>
          <span class="margin-auto">
            <Email-confidence-card class="fn-13 linear-background" />
          </span>
        </div>
      </div>
      <div class="p-t-10p flex-c">
        <div class="txt-blue flex-c">
          <Phone-Card :loading="loading" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PhoneCard from './PhoneCard'
import emailConfidenceCard from './EmailConfidenceCard'

import UpgradePopup from '@/components/UpgradePopup.vue'
import ActivationPopup from '@/components/ActivationPopup.vue'
import companyIcon from '@/assets/icons/svg/company.svg'
import emailIcon from '@/assets/icons/svg/email.svg'
import copyIcon from '@/assets/icons/svg/copy.svg'
import { post, get } from '@/assets/util/rest.js'
import sharedFunctions from '@/assets/util/functions.js'

import findEmailButton from '@/components/FindEmailButton'
import addDomainFindEmail from '@/components/AddDomainFindEmail'
import { MxEvent } from '@/assets/util/constants/analytics.js'
import LinkedinConnectVue from '@/components/LinkedinConnect.vue'

export default {
  name: 'ContactCard',
  props: [
    'profileData',
    'loading',
    'showUpgradePopup',
    'enableReviewPopup',
    'requestId'
  ],
  watch: {
    profileData: {
      handler(newValue, oldValue) {
        if (newValue && oldValue != newValue && newValue.email == null) {
          this.findEmail()
        }
      }
    }
  },
  computed: {
    isContactOwned: function() {
      const id = this.profileData.linkedinId
      return this.$store.state.linkedin.bulk.contacts[id].contactAlreadyOwned
    }
  },
  updated() {
    if (this.profileData) {
      const id = this.profileData.linkedinId
      if (this.profileData.selectedOtherMatch) {
        if (
          this.profileData.domain == null ||
          (this.smptCompleted && this.profileData.email == null)
        ) {
          this.$store.state.linkedin.bulk.contacts[
            id
          ] = this.profileData.selectedOtherMatch
        }
      }
    }
  },
  data: function() {
    return {
      errorMsg: null,
      smptCompleted: false,
      showPopup: false,
      showCopyMsg: false,
      showActivationPopup: false,
      showLinkedInConnect: false
    }
  },
  components: {
    'Phone-Card': PhoneCard,
    'Email-confidence-card': emailConfidenceCard,
    'Company-Icon': companyIcon,
    'Email-Icon': emailIcon,
    'Find-Email-Button': findEmailButton,
    'Add-Domain-Find-Email-But': addDomainFindEmail,
    'Copy-Icon': copyIcon,
    'Upgrade-Popup': UpgradePopup,
    'Activation-Popup': ActivationPopup,
    'Linkedin-Connect-Popup': LinkedinConnectVue
  },
  methods: {
    showActivationError() {
      this.showActivationPopup = true
    },
    showLinkedInConnectPrompt() {
      this.showLinkedInConnect = true
    },
    updatePhoneAndEmailFromSmtp(res) {
      this.smptCompleted = true
      this.updatePhoneAndEmail(res)
    },
    updatePhoneAndEmail(res) {
      this.errorMsg = null
      if (this.profileData && this.profileData.linkedinId && res) {
        const id = this.profileData.linkedinId
        this.$store.state.linkedin.bulk.contacts[id].email = res.email
        this.$store.state.linkedin.bulk.contacts[id].contactAlreadyOwned = true
        this.$store.state.linkedin.bulk.contacts[id].emailDeliverability =
          res.emailDeliverability
        this.$store.state.linkedin.bulk.contacts[id].phone = res.phone
        this.$store.state.linkedin.bulk.contacts[id].mcId = res.masterContactId
      }
    },
    findEmail(autoTrigger) {
      if (this.profileData && this.profileData.domain) {
        const reqData = this.constructReqData(this.profileData)
        post('li/adv/profile.htm', reqData, this.requestId)
          .then(response => {
            if (response.status == 200) {
              response.json().then(res => {
                if (res) {
                  //PARAMS -> linkedinId
                  const dataMap = {
                    uniqueId: this.$store.state.linkedin.bulk.unique_id,
                    isSalesNavigator: this.$store.state.isSalesNavigator
                  }
                  if (res && res.statusCode === '200') {
                    sharedFunctions.track(
                      MxEvent.bulkContactEmailFound,
                      dataMap,
                      this.$store.state
                    )
                    this.updatePhoneAndEmail(res.response)
                    if (autoTrigger)
                      this.$store.state.linkedin.autoTrigger = true
                    //get email is automatically clicked
                  } else if (res && res.statusCode === '100') {
                    this.errorMsg = sharedFunctions.getRespectiveErrorMsg(res)
                    dataMap.error = this.errorMsg
                    sharedFunctions.track(
                      MxEvent.bulkContactEmailNotFound,
                      dataMap,
                      this.$store.state
                    )
                  } else {
                    dataMap.error = 'Server Error'
                    sharedFunctions.track(
                      MxEvent.bulkContactEmailNotFound,
                      dataMap,
                      this.$store.state
                    )
                  }
                }
              })
            }
          })
          .catch(e => {
            console.log('error: ' + e)
          })
      }
    },
    findEmailWithCustomDomain(inputData) {
      if (this.profileData) {
        const id = this.profileData.linkedinId
        this.profileData.isVerifiedDomain = false
        this.profileData.isActivelyWorking = true
        this.profileData.domain = inputData.domain
        this.$store.state.linkedin.bulk.contacts[id].domain = inputData.domain
        this.$store.state.linkedin.bulk.contacts[id].companyName =
          inputData.companyName
        this.findEmail(true)
      }
    },
    constructReqData(profile) {
      let reqData = {}
      //reqData['linkedinId'] = profile['linkedinId']
      reqData['fullName'] = profile['fullName']
      reqData['address'] = profile['address']
      reqData['companyName'] = profile['companyName']
      reqData['domain'] = profile['domain']
      reqData['title'] = profile['title']
      reqData['verifiedDomain'] = profile['isVerifiedDomain']
      reqData['companyUrl'] = profile['companyUrl']
      reqData.salesNavigator = profile.isSalesNavigator || false
      return reqData
    },
    canShowButton() {
      return (
        this.profileData &&
        this.profileData.mcId &&
        this.profileData.email.indexOf('*') != -1
      )
    },
    copyClipboard(cId, mail, name) {
      let txtDisplay = 'Copied'
      const dataMap = {
        pageType: 'Bulk'
      }
      sharedFunctions.clickToCopy(
        cId,
        mail,
        name,
        null,
        dataMap,
        txtDisplay,
        this.$store.state
      )
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.contact-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 15px 10px;
  color: $grey-color;
  .title {
    line-height: 22px;
  }
  .phonecard-grid {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    grid-row-gap: 7px;
    grid-column-gap: 7px;
  }

  .error {
    @extend .fn-12;
    @extend .p-t-10p;
    color: $orange;
  }
  .no-company {
    border-radius: 18.5px;
    background-color: #f2f2f2;
    color: $grey-color;
    padding: 3px 10px;
    font-size: 12px;
    width: fit-content;
    margin-top: 5px;
    cursor: pointer;
  }
  .no-domain {
    border-radius: 18.5px;
    color: $grey-color;
    background-color: #f2f2f2;
    padding: 3px 10px;
    font-size: 12px;
    width: fit-content;
    margin-top: 5px;
    cursor: pointer;
  }
  .no-domain::v-deep,
  .no-company::v-deep {
    svg {
      width: 13px;
    }
    path {
      fill: #636a72;
    }
  }

  .company-name {
    padding: 12px 0 5px 0;
    span {
      @extend .icon-img-spacing;
      @extend .ellipsis;
      min-width: 120px;
      max-width: 180px;
      &.linear-background {
        min-width: 150px;
      }
    }
  }
  .top-section {
    width: 100%;
    justify-content: space-between;
  }
  .name {
    font-size: 14px;
    @extend .ellipsis;
  }
  .title {
    color: $grey-color;
    font-size: 13px;
    @extend .ellipsis;
  }
  .email-hldr {
    padding-right: 10px;
    color: $primary-color;
    svg {
      fill: $primary-color;
    }
    .email {
      @extend .icon-img-spacing;
      font-size: 13px;
      word-break: break-word;
      &.linear-background {
        min-width: 150px;
      }
    }
    .copy-icon {
      fill: #8e9398;
      width: 12px;
      height: 13px;
      cursor: pointer;
    }
    .copy-section {
      position: relative;
      padding-left: 5px;
    }
    .copied-success {
      color: #484848;
      display: none;
      font-size: 11px;
      position: absolute;
      top: 20px;
      width: 40px;
      left: 5px;
    }
    .show {
      display: block;
    }
  }
  .c-grey {
    color: $grey-color;
  }
  .left-section {
    max-width: 200px;
    min-width: 170px;
    @extend .ellipsis;
  }
  .line-1-25 {
    line-height: 1.25;
  }

  .but-holder {
    align-items: start;
    justify-content: flex-end;
    button {
      width: 70px;
      height: 20px;
      border-radius: 3px;
    }
  }
}
</style>
